import React, { useEffect, useState, useRef } from "react";
import { Upload, FileText, Download } from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { DataTable, Toolbar, SearchInput, Btn, Field, Select } from "@/components/Primitives";
import { api, API, formatApiErrorDetail, fmtDate, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CATEGORIES = [
  "delivery_note", "weighbridge_slip", "photo", "processing_doc",
  "customer_order", "export_doc", "invoice", "statement",
  "transport_doc", "container_doc", "proof_of_delivery", "other",
];

export default function Documents() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const r = await api.get("/documents");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (category) fd.append("category", category);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = (id, name) => {
    const a = document.createElement("a");
    a.href = `${API}/documents/${id}/download`;
    a.target = "_blank";
    a.rel = "noreferrer";
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const filtered = items.filter((d) =>
    (!q || d.original_filename.toLowerCase().includes(q.toLowerCase()) || (d.category || "").includes(q.toLowerCase()))
  );

  return (
    <AppLayout>
      <PageHeader
        title="Documents"
        subtitle="Delivery notes, photos, invoices, container papers — all linked to source records."
        actions={can(user?.role, "documents:create") && (
          <>
            <Select value={category} onChange={(e) => setCategory(e.target.value)} className="max-w-[220px]" data-testid="upload-category">
              <option value="">— Category —</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </Select>
            <input ref={fileRef} type="file" onChange={onUpload} className="hidden" data-testid="upload-file-input" />
            <Btn onClick={() => fileRef.current?.click()} disabled={uploading} testid="upload-btn">
              <Upload className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload"}
            </Btn>
          </>
        )}
      />

      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search documents..." />
        <div className="text-xs text-stone-500 ml-auto">{filtered.length} documents</div>
      </Toolbar>

      <DataTable
        columns={[
          { header: "File", render: (r) => (
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-stone-400" />
              <span className="font-medium">{r.original_filename}</span>
            </div>
          )},
          { header: "Category", render: (r) => r.category ? <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-sm bg-stone-100">{r.category.replace(/_/g, " ")}</span> : "—" },
          { header: "Linked To", render: (r) => r.linked_to ? <span className="text-xs">{r.linked_to} · {r.linked_id?.slice(0, 8)}…</span> : "—" },
          { header: "Size", mono: true, align: "right", render: (r) => `${Math.round((r.size || 0) / 1024)} KB` },
          { header: "Uploaded", mono: true, render: (r) => fmtDate(r.created_at) },
          { header: "", render: (r) => (
            <button onClick={() => download(r.id, r.original_filename)} className="text-emerald-800 hover:text-emerald-900" data-testid={`download-${r.id}`}>
              <Download className="h-4 w-4" />
            </button>
          )},
        ]}
        rows={filtered}
      />
    </AppLayout>
  );
}
